import type { SupportedLanguage } from '@/libs/i18n';
import { COUNTRY_OPTIONS, INDUSTRY_OPTIONS } from './options';

export const HUBSPOT_FORM_IDS: Record<SupportedLanguage, string> = {
  en: '4d39d174-8298-4f13-a922-3d9cb937d0ad',
  fr: '614f2105-7b5d-4c0a-988c-662ad2e2e864',
  es: '4c797bbf-c519-4ed0-87b2-54221b46ffcb',
  de: '1a7887f1-f7de-4fa6-9f4e-7d67ce40c170'
};

export const FORM_PROPERTIES = [
  { objectTypeId: '0-1', name: 'firstname', reference: '0-1/firstname' },
  { objectTypeId: '0-1', name: 'lastname', reference: '0-1/lastname' },
  { objectTypeId: '0-1', name: 'email', reference: '0-1/email' },
  { objectTypeId: '0-1', name: 'phone', reference: '0-1/phone' },
  { objectTypeId: '0-2', name: 'name', reference: '0-2/name' },
  { objectTypeId: '0-1', name: 'country_select_eezi', reference: '0-1/country_select_eezi' },
  { objectTypeId: '0-1', name: 'jobtitle', reference: '0-1/jobtitle' },
  { objectTypeId: '0-1', name: 'industry_select_eezi', reference: '0-1/industry_select_eezi' },
  { objectTypeId: '0-1', name: 'message', reference: '0-1/message' }
] as const;

export type FormPropertyName = (typeof FORM_PROPERTIES)[number]['name'];

type IndustryValue = (typeof INDUSTRY_OPTIONS)[number]['value'];
type LocalizedLanguage = Exclude<SupportedLanguage, 'en'>;

type FormCopy = {
  fields: Record<FormPropertyName, string>;
  phoneCountry: string;
  submissionError: string;
  submit: string;
  submitting: string;
  success: string;
};

export const FORM_COPY: Record<SupportedLanguage, FormCopy> = {
  en: {
    fields: {
      firstname: 'First Name',
      lastname: 'Last name',
      email: 'Email',
      phone: 'Phone number',
      name: 'Company',
      country_select_eezi: 'Country',
      jobtitle: 'Job Title',
      industry_select_eezi: 'Industry',
      message: 'Message'
    },
    phoneCountry: 'Country code',
    submissionError: 'The form could not be submitted. Please check the highlighted fields and try again.',
    submit: 'Submit',
    submitting: 'Submitting…',
    success: 'Thank you. We will be in touch soon.'
  },
  fr: {
    fields: {
      firstname: 'Prénom',
      lastname: 'Nom de famille',
      email: 'E-mail',
      phone: 'Numéro de téléphone',
      name: 'Entreprise',
      country_select_eezi: 'Pays',
      jobtitle: 'Intitulé du poste',
      industry_select_eezi: 'Secteur',
      message: 'Message'
    },
    phoneCountry: 'Indicatif pays',
    submissionError: 'Le formulaire n’a pas pu être envoyé. Vérifiez les champs signalés et réessayez.',
    submit: 'Soumettre',
    submitting: 'Envoi…',
    success: 'Merci. Nous vous contacterons bientôt.'
  },
  es: {
    fields: {
      firstname: 'Nombre',
      lastname: 'Apellido',
      email: 'Correo electrónico',
      phone: 'Número de teléfono',
      name: 'Empresa',
      country_select_eezi: 'País',
      jobtitle: 'Cargo',
      industry_select_eezi: 'Sector',
      message: 'Mensaje'
    },
    phoneCountry: 'Código de país',
    submissionError: 'No se pudo enviar el formulario. Revise los campos indicados e inténtelo de nuevo.',
    submit: 'Enviar',
    submitting: 'Enviando…',
    success: 'Gracias. Nos pondremos en contacto pronto.'
  },
  de: {
    fields: {
      firstname: 'Vorname',
      lastname: 'Nachname',
      email: 'E-Mail',
      phone: 'Telefonnummer',
      name: 'Unternehmen',
      country_select_eezi: 'Land',
      jobtitle: 'Stellenbezeichnung',
      industry_select_eezi: 'Branche',
      message: 'Nachricht'
    },
    phoneCountry: 'Ländervorwahl',
    submissionError:
      'Das Formular konnte nicht gesendet werden. Prüfen Sie die markierten Felder und versuchen Sie es erneut.',
    submit: 'Senden',
    submitting: 'Wird gesendet…',
    success: 'Vielen Dank. Wir melden uns in Kürze.'
  }
};

const COUNTRY_CODE_ALIASES: Record<string, string> = {
  'Antigua and Barbuda': 'AG',
  'Bolivia, Plurinational State of': 'BO',
  'Bonaire, Sint Eustatius and Saba': 'BQ',
  'Bosnia and Herzegovina': 'BA',
  'Brunei Darussalam': 'BN',
  'Congo, the Democratic Republic of the': 'CD',
  'Falkland Islands (Malvinas)': 'FK',
  'Heard Island and McDonald Islands': 'HM',
  'Holy See (Vatican City State)': 'VA',
  'Hong Kong': 'HK',
  'Iran, Islamic Republic of': 'IR',
  "Lao People's Democratic Republic": 'LA',
  Myanmar: 'MM',
  Pitcairn: 'PN',
  'Saint Barthélemy': 'BL',
  'Saint Helena, Ascension and Tristan da Cunha': 'SH',
  'Saint Kitts and Nevis': 'KN',
  'Saint Lucia': 'LC',
  'Saint Martin (French part)': 'MF',
  'Saint Pierre and Miquelon': 'PM',
  'Saint Vincent and the Grenadines': 'VC',
  'Sao Tome and Principe': 'ST',
  'Sint Maarten (Dutch part)': 'SX',
  'South Georgia and the South Sandwich Islands': 'GS',
  'Svalbard and Jan Mayen': 'SJ',
  Swaziland: 'SZ',
  'Trinidad and Tobago': 'TT',
  Turkey: 'TR',
  'Turks and Caicos Islands': 'TC',
  'Venezuela, Bolivarian Republic of': 'VE',
  'Wallis and Futuna': 'WF'
};

const INDUSTRY_LABELS: Record<LocalizedLanguage, Record<IndustryValue, string>> = {
  fr: {
    Agriculture: 'Agriculture',
    Apparel: 'Habillement',
    Banking: 'Secteur bancaire',
    Biotechnology: 'Biotechnologie',
    Chemicals: 'Produits chimiques',
    Communications: 'Communication',
    Construction: 'Construction',
    Consulting: 'Conseil',
    Education: 'Éducation',
    Electronics: 'Électronique',
    Energy: 'Énergie',
    Engineering: 'Ingénierie',
    Entertainment: 'Divertissement',
    Environmental: 'Environnement',
    Finance: 'Finance',
    'Food & Beverage': 'Alimentation et boissons',
    Government: 'Administration publique',
    Healthcare: 'Santé',
    Hospitality: 'Hôtellerie',
    Insurance: 'Assurance',
    Machinery: 'Machines',
    Manufacturing: 'Industrie manufacturière',
    Media: 'Médias',
    'Not For Profit': 'Organisation à but non lucratif',
    Recreation: 'Loisirs',
    Retail: 'Commerce de détail',
    Shipping: 'Livraison',
    Technology: 'Technologie',
    Telecommunications: 'Télécommunications',
    Transportation: 'Transport',
    Utilities: 'Services publics',
    Other: 'Autre'
  },
  de: {
    Agriculture: 'Landwirtschaft',
    Apparel: 'Bekleidung',
    Banking: 'Bankwesen',
    Biotechnology: 'Biotechnologie',
    Chemicals: 'Chemie',
    Communications: 'Kommunikation',
    Construction: 'Bauwesen',
    Consulting: 'Beratung',
    Education: 'Bildung',
    Electronics: 'Elektronik',
    Energy: 'Energie',
    Engineering: 'Ingenieurwesen',
    Entertainment: 'Unterhaltung',
    Environmental: 'Umwelt',
    Finance: 'Finanzen',
    'Food & Beverage': 'Lebensmittel und Getränke',
    Government: 'Öffentlicher Sektor',
    Healthcare: 'Gesundheitswesen',
    Hospitality: 'Gastgewerbe',
    Insurance: 'Versicherungen',
    Machinery: 'Maschinenbau',
    Manufacturing: 'Fertigung',
    Media: 'Medien',
    'Not For Profit': 'Gemeinnützige Organisation',
    Recreation: 'Freizeit',
    Retail: 'Einzelhandel',
    Shipping: 'Versand',
    Technology: 'Technologie',
    Telecommunications: 'Telekommunikation',
    Transportation: 'Transport',
    Utilities: 'Versorgungsunternehmen',
    Other: 'Sonstiges'
  },
  es: {
    Agriculture: 'Agricultura',
    Apparel: 'Ropa',
    Banking: 'Sector bancario',
    Biotechnology: 'Biotecnología',
    Chemicals: 'Productos químicos',
    Communications: 'Comunicación',
    Construction: 'Construcción',
    Consulting: 'Consultoría',
    Education: 'Educación',
    Electronics: 'Electrónica',
    Energy: 'Energía',
    Engineering: 'Ingeniería',
    Entertainment: 'Entretenimiento',
    Environmental: 'Medio ambiente',
    Finance: 'Finanzas',
    'Food & Beverage': 'Alimentación y bebidas',
    Government: 'Gobierno',
    Healthcare: 'Salud',
    Hospitality: 'Hostelería',
    Insurance: 'Seguros',
    Machinery: 'Maquinaria',
    Manufacturing: 'Manufactura',
    Media: 'Medios de comunicación',
    'Not For Profit': 'Organización sin ánimo de lucro',
    Recreation: 'Ocio',
    Retail: 'Comercio minorista',
    Shipping: 'Envíos',
    Technology: 'Tecnología',
    Telecommunications: 'Telecomunicaciones',
    Transportation: 'Transporte',
    Utilities: 'Servicios públicos',
    Other: 'Otros'
  }
};

const normalizeCountryName = (value: string) =>
  value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const englishRegionNames = new Intl.DisplayNames(['en'], { type: 'region' });
const countryCodeByName = new Map<string, string>();

for (let first = 65; first <= 90; first += 1) {
  for (let second = 65; second <= 90; second += 1) {
    const code = String.fromCharCode(first, second);
    const name = englishRegionNames.of(code);
    if (name && name !== code) countryCodeByName.set(normalizeCountryName(name), code);
  }
}

Object.entries(COUNTRY_CODE_ALIASES).forEach(([name, code]) => {
  countryCodeByName.set(normalizeCountryName(name), code);
});

const countryNamesByLanguage = new Map<SupportedLanguage, Intl.DisplayNames>();
const countryOptionsByLanguage = new Map<SupportedLanguage, { label: string; value: string }[]>();
const industryOptionsByLanguage = new Map<SupportedLanguage, { label: string; value: IndustryValue }[]>();

export const getLocalizedCountryName = (language: SupportedLanguage, code: string, fallback: string): string => {
  let countryNames = countryNamesByLanguage.get(language);
  if (!countryNames) {
    countryNames = new Intl.DisplayNames([language], { type: 'region' });
    countryNamesByLanguage.set(language, countryNames);
  }
  return countryNames.of(code) ?? fallback;
};

export const getCountrySelectOptions = (language: SupportedLanguage) => {
  const cached = countryOptionsByLanguage.get(language);
  if (cached) return cached;

  const options = COUNTRY_OPTIONS.map((value) => {
    const code = countryCodeByName.get(normalizeCountryName(value));
    return { label: code ? getLocalizedCountryName(language, code, value) : value, value };
  });
  countryOptionsByLanguage.set(language, options);
  return options;
};

export const getIndustrySelectOptions = (language: SupportedLanguage) => {
  const cached = industryOptionsByLanguage.get(language);
  if (cached) return cached;

  const options = INDUSTRY_OPTIONS.map((option) => ({
    label: language === 'en' ? option.label : INDUSTRY_LABELS[language][option.value],
    value: option.value
  }));
  industryOptionsByLanguage.set(language, options);
  return options;
};

export const getHubspotLanguage = (language?: string): SupportedLanguage => {
  const code = language?.slice(0, 2).toLowerCase();
  return code === 'fr' || code === 'es' || code === 'de' ? code : 'en';
};
